/**
 * Projection helpers: turn internal stored records into the exact DTO shapes
 * the frontend services are expected to return.
 */

import {
  ApplicationWithAdminData,
  EvaluationSummaryDto,
  ScoreStats,
} from '../types/admin';
import {
  ApplicationStatus,
  ApplicationWithEvaluations,
  FileResponseDto,
  SectionBasedApplication,
  SectionType,
} from '../types';
import { Evaluator, EvaluatorAssignment } from '../types/evaluator';
import { db } from './store';
import { CRITERIA_KEYS, StoredApplication, StoredAssignment, StoredFile, StoredUser } from './seed';

export const findUser = (id: string): StoredUser | undefined =>
  db.users.find((u) => u.id === id);

export const findApp = (id: string): StoredApplication | undefined =>
  db.applications.find((a) => a.id === id);

export const findTemplate = (id: string): any =>
  db.formTemplates.find((t) => t.id === id);

const userSummary = (id: string) => {
  const u = findUser(id);
  return { id, name: u?.name ?? 'Usuario', email: u?.email ?? '' };
};

const templateSummary = (id: string) => {
  const t = findTemplate(id);
  return {
    id,
    name: t?.name ?? 'Concurso',
    description: t?.description ?? '',
    sections: t?.sections,
    deadline: t?.deadline,
    isActive: t?.isActive ?? true,
    isPublished: t?.isPublished ?? true,
    year: t?.year ?? null,
  };
};

export const toFileResponseDto = (f: StoredFile): FileResponseDto => ({
  id: f.id,
  applicationId: f.applicationId,
  sectionType: f.sectionType,
  filename: f.filename,
  originalName: f.originalName,
  mimeType: f.mimeType,
  size: f.size,
  status: 'COMPLETED',
  uploadedAt: new Date(f.uploadedAt),
  downloadUrl: f.downloadUrl,
});

export const filesForApp = (applicationId: string): FileResponseDto[] =>
  db.files.filter((f) => f.applicationId === applicationId).map(toFileResponseDto);

export const toSectionBasedApplication = (
  app: StoredApplication
): SectionBasedApplication => ({
  id: app.id,
  userId: app.userId,
  formTemplateId: app.formTemplateId,
  applicationNumber: app.applicationNumber,
  status: app.status as ApplicationStatus,
  answers: {
    sectionAnswers: app.sectionAnswers as Record<SectionType, any>,
    sectionStatus: app.sectionStatus as Record<SectionType, any>,
    lastSaved: app.lastSaved,
    complianceConfirmed: app.complianceConfirmed,
  },
  submittedAt: app.submittedAt,
  createdAt: app.createdAt,
  updatedAt: app.updatedAt,
  user: userSummary(app.userId),
  formTemplate: templateSummary(app.formTemplateId),
  files: filesForApp(app.id),
  hasEvaluations: db.evaluations.some((e) => e.applicationId === app.id),
  awardNotifiedAt: app.awardNotifiedAt,
});

export const toEvaluatorAssignment = (a: StoredAssignment): EvaluatorAssignment => {
  const evaluation = db.evaluations.find((e) => e.assignmentId === a.id);
  const ev = findUser(a.evaluatorId);
  return {
    id: a.id,
    applicationId: a.applicationId,
    evaluatorId: a.evaluatorId,
    assignedBy: a.assignedBy,
    assignedAt: a.assignedAt,
    acceptedAt: a.acceptedAt ?? (a.status === 'pending' ? null : a.assignedAt),
    status: a.status,
    evaluationSubmittedAt: a.evaluationSubmittedAt,
    conflictDeclared: a.conflictDeclared,
    conflictStatus: a.conflictStatus,
    conflictDetails: a.conflictDetails,
    conflictDeclaredAt: a.conflictDeclaredAt,
    declineReason: a.declineReason ?? null,
    declineNote: a.declineNote ?? null,
    evaluator: {
      id: a.evaluatorId,
      name: ev?.name ?? 'Evaluador',
      email: ev?.email ?? '',
      isExternal: ev?.isExternal ?? false,
      isConfirmed: ev?.isConfirmed ?? true,
    },
    assignedByUser: userSummary(a.assignedBy),
    evaluation: evaluation
      ? {
          id: evaluation.id,
          submittedAt: evaluation.submittedAt,
        }
      : undefined,
  };
};

export const toEvaluator = (u: StoredUser): Evaluator => {
  const assignments = db.assignments.filter((a) => a.evaluatorId === u.id);
  const completed = assignments.filter((a) => a.status === 'completed').length;
  return {
    id: u.id,
    name: u.name,
    email: u.email,
    role: 'evaluator',
    isExternal: u.isExternal,
    isConfirmed: u.isConfirmed,
    lastLoginAt: u.lastLoginAt ?? null,
    userProfile: u.userProfile ?? null,
    evaluationStats: {
      totalAssignments: assignments.length,
      completedEvaluations: completed,
      pendingEvaluations: assignments.length - completed,
    },
  };
};

// --- Evaluation summary (admin) ----------------------------------------------

const stats = (values: number[]): ScoreStats => {
  if (values.length === 0) {
    return { min: 0, max: 0, average: 0, standardDeviation: 0, count: 0 };
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const average = values.reduce((a, b) => a + b, 0) / values.length;
  const variance =
    values.reduce((a, b) => a + Math.pow(b - average, 2), 0) / values.length;
  return {
    min,
    max,
    average: Math.round(average * 100) / 100,
    standardDeviation: Math.round(Math.sqrt(variance) * 100) / 100,
    count: values.length,
  };
};

export const buildEvaluationSummary = (applicationId: string): EvaluationSummaryDto => {
  const evaluations = db.evaluations.filter((e) => e.applicationId === applicationId);
  const submitted = evaluations.filter((e) => e.submittedAt);

  const criteriaDistribution: any = {};
  CRITERIA_KEYS.forEach((key) => {
    const scores = submitted
      .map((e) => e.criteriaEvaluations[key]?.score)
      .filter((s): s is number => typeof s === 'number');
    criteriaDistribution[key] = stats(scores);
  });

  const weighted = submitted.map((e) => {
    const scores = CRITERIA_KEYS.map((k) => e.criteriaEvaluations[k]?.score ?? 0);
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  });

  const assignments = db.assignments.filter((a) => a.applicationId === applicationId);

  return {
    criteriaDistribution,
    weightedScoreDistribution: stats(weighted),
    numberOfEvaluations: submitted.length,
    numberOfEvaluators: assignments.filter(
      (a) => a.status !== 'declined' && a.status !== 'conflict_declared'
    ).length,
    completionPercentage: (() => {
      const active = assignments.filter(
        (a) => a.status !== 'declined' && a.status !== 'conflict_declared'
      ).length;
      return active === 0 ? 0 : Math.round((submitted.length / active) * 100);
    })(),
  };
};

export const weightedScore = (
  criteriaEvaluations: Record<string, { score: number }>
): number => {
  const scores = CRITERIA_KEYS.map((k) => criteriaEvaluations[k]?.score ?? 0);
  return Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100;
};

export const toAdminApplication = (app: StoredApplication): ApplicationWithAdminData => {
  const base = toSectionBasedApplication(app);
  const assignments = db.assignments
    .filter((a) => a.applicationId === app.id)
    .map(toEvaluatorAssignment);
  const u = findUser(app.userId);
  return {
    ...base,
    status: app.status,
    user: {
      id: app.userId,
      name: u?.name ?? 'Usuario',
      email: u?.email ?? '',
      userProfile: u?.userProfile,
    },
    reviewerAssignment: app.reviewerAssignment,
    statusHistory: app.statusHistory,
    evaluatorAssignments: assignments,
    evaluationSummary: buildEvaluationSummary(app.id),
    awardNotifiedAt: app.awardNotifiedAt,
  } as ApplicationWithAdminData;
};

export const toApplicationWithEvaluations = (
  app: StoredApplication
): ApplicationWithEvaluations => {
  const evaluations = db.evaluations
    .filter((e) => e.applicationId === app.id && e.submittedAt)
    .map((e) => ({
      id: e.id,
      criteriaEvaluations: Object.fromEntries(
        Object.entries(e.criteriaEvaluations).map(([k, v]) => [
          k,
          { score: v.score, maxScore: v.maxScore, comments: v.comments },
        ])
      ),
      budgetComments: e.budgetComments,
      overallComments: e.overallComments,
      conflictsDeclared: e.conflictsDeclared,
      conflictDetails: e.conflictDetails,
      submittedAt: e.submittedAt,
      createdAt: e.createdAt,
      updatedAt: e.updatedAt,
      finalScore: weightedScore(e.criteriaEvaluations),
    }));

  const u = findUser(app.userId);
  return {
    id: app.id,
    applicationNumber: app.applicationNumber,
    status: 'evaluated',
    submittedAt: app.submittedAt ?? app.createdAt,
    createdAt: app.createdAt,
    updatedAt: app.updatedAt,
    user: { id: app.userId, name: u?.name ?? 'Usuario', email: u?.email ?? '' },
    formTemplate: {
      id: app.formTemplateId,
      name: findTemplate(app.formTemplateId)?.name ?? 'Concurso',
      description: findTemplate(app.formTemplateId)?.description ?? '',
      deadline: findTemplate(app.formTemplateId)?.deadline ?? '',
      isActive: findTemplate(app.formTemplateId)?.isActive ?? true,
    },
    answers: {
      sectionAnswers: app.sectionAnswers,
      sectionStatus: app.sectionStatus,
    },
    evaluations,
    files: filesForApp(app.id),
    evaluationSummary: buildEvaluationSummary(app.id),
  };
};
